import { useEffect, useState } from "react";
import { Modal } from "@/common/components/modal";
import { Button } from "@/common/components/button";
import Table from "@/common/components/Table";
import {
  COMPANY_PRODUCTS,
  IMPORTER_POST_URL,
  MANUFACTURER_POST_URL,
  ORDERS_URL,
} from "@/common/constants";
import { ModalFormWrapper, ModalWrapper } from "@/common/elements";
import { KeyValueObj } from "@/common/types";
import { pluralize } from "@/common/utils/general";
import { beforeToday } from "@/common/utils/isContain";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import useAxiosCall from "@/hooks/useAxiosCall";
import {
  getManuImporterTableData,
  manuImporterTableHeaders,
  manuImporterTableKeys,
  tableHeaders,
  tableKeys,
} from "./subs/data";
import { OrderDeliveryDetails, ReqForm } from "./subs/ReqForm";
import { buildOrderLine } from "./subs/buildOrderLine";
import { ReqWrapper } from "./subs/styles";
import { useNavigate } from "react-router-dom";
import { PageTitleNavs } from "@/common/components/page-title-navs";
import useQuery from "@/hooks/useQuery";
import { Pagination } from "@/common/components/pagination";
import Tab from "@/common/components/Tab/Tab";
import AllProductsOrder from "./subs/orderByProductSearch/all-products-order";
import { PiCaretLeftBold, PiCaretRightBold } from "react-icons/pi";

export interface IRequisitionProps {}

const tabs = ["order by manufacturer", "all products"];

// Shared between stage 2 (product details) and stage 4 (preview) — an order
// can run to dozens of products, so both stages show one at a time in a
// sliding carousel instead of stacking every ReqForm in one scroll.
function ProductCarouselTrack({
  products,
  activeIndex,
  children,
}: {
  products: KeyValueObj[];
  activeIndex: number;
  children: (product: KeyValueObj, index: number) => React.ReactNode;
}) {
  return (
    <div className="w-full overflow-hidden">
      <div
        className="flex w-full transition-transform duration-300 ease-in-out"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {products.map((product, i) => (
          <div key={product?.id ?? i} className="w-full shrink-0">
            {children(product, i)}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProductCarouselNav({
  products,
  activeIndex,
  onIndexChange,
}: {
  products: KeyValueObj[];
  activeIndex: number;
  onIndexChange: (index: number) => void;
}) {
  if (products.length <= 1) return null;

  return (
    <div className="flex w-full items-center justify-center gap-2">
      <button
        type="button"
        onClick={() => onIndexChange(Math.max(activeIndex - 1, 0))}
        disabled={activeIndex === 0}
        className="flex h-8 w-8 items-center justify-center rounded text-neutral-600 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-300 disabled:hover:bg-transparent"
      >
        <PiCaretLeftBold className="text-lg" />
      </button>
      <div className="flex flex-wrap items-center justify-center gap-1">
        {products.map((product, i) => (
          <button
            key={product?.id ?? i}
            type="button"
            onClick={() => onIndexChange(i)}
            className={`rounded px-3 py-1 text-sm font-medium ${
              i === activeIndex
                ? "bg-[#33145f] text-white"
                : "text-neutral-600 hover:bg-neutral-100"
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() =>
          onIndexChange(Math.min(activeIndex + 1, products.length - 1))
        }
        disabled={activeIndex === products.length - 1}
        className="flex h-8 w-8 items-center justify-center rounded text-neutral-600 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:text-neutral-300 disabled:hover:bg-transparent"
      >
        <PiCaretRightBold className="text-lg" />
      </button>
    </div>
  );
}

export function MakeOrder(_: IRequisitionProps) {
  //STATES
  const [view, setView] = useState(tabs[0]);
  const [selectedList, setSelectedList] = useState<KeyValueObj[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<KeyValueObj[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalSubtitle, setModalSubtitle] = useState("");
  const [stage, setStage] = useState(1);
  const [tableData, setTableData] = useState<KeyValueObj[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<KeyValueObj[]>([]);
  // Which product the stage-2 slider is currently showing — picking 10
  // products and scrolling a stacked list of forms doesn't work, so stage 2
  // shows one product's form at a time with Prev/Next.
  const [productIndex, setProductIndex] = useState(0);
  const [orders, setOrders] = useState<KeyValueObj>({});
  const [deliveryDetails, setDeliveryDetails] = useState<KeyValueObj>({});
  const [isOrdersValid, setIsOrdersValid] = useState(false);
  const [isDeliveryValid, setIsDeliveryValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [retry, setRetry] = useState(0);

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [companySearchTerm, setCompanySearchTerm] = useState("");

  // MED-708: a supplier's shelf is served 25 at a time, so the product table
  // inside the modal drives its own page/per-page/search — separate from the
  // `page`/`perPage` above, which belong to the company table on the page
  // behind it.
  const [productPage, setProductPage] = useState(1);
  const [productPerPage, setProductPerPage] = useState(25);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  // Every product this buyer has loaded for the current supplier, keyed by
  // id. `fetchedProducts` only ever holds the page in view, so anything that
  // resolves a ticked row back to its full record (stage 2, the order
  // payload) has to read from here — otherwise ticking a product on page one
  // and paging forward drops it without saying so.
  const [productsById, setProductsById] = useState<KeyValueObj>({});
  const [shelfCompanyId, setShelfCompanyId] = useState<string | undefined>();

  const selectedCompanyId = selectedCompany?.[0]?.id;

  // A different supplier is a different shelf: the page, the search, the
  // products loaded so far and anything already ticked all belonged to the
  // previous one, and an order carries a single `supplier_id`.
  //
  // Done during render rather than in an effect on purpose. An effect resets
  // AFTER the fetch effect has already run for this commit, so switching
  // supplier fired one request for the new supplier at the old page and a
  // second once the reset landed — two of a request that takes seconds. This
  // is React's "adjust state when an input changes" pattern: it re-renders
  // immediately, before any effect sees the stale page.
  if (selectedCompanyId !== shelfCompanyId) {
    setShelfCompanyId(selectedCompanyId);
    setProductPage(1);
    setProductSearchTerm("");
    setProductsById({});
    setSelectedList([]);
  }

  //HOOKES
  const [
    getProducts,
    fetchedProducts,
    productsError,
    productsLoading,
    productsMeta,
  ] = useAxiosCall();
  const {
    loading: impotersLoading,
    error: importersError,
    data: importer,
  } = useQuery(
    IMPORTER_POST_URL +
      `?page=${page}&per_page=${perPage}&name=${encodeURIComponent(companySearchTerm)}`,
  );

  const {
    data: manufacturers,
    loading: manufacturersLoading,
    error: manufacturersError,
    meta: metaData,
  } = useQuery(
    MANUFACTURER_POST_URL +
      `?page=${page}&per_page=${perPage}&name=${encodeURIComponent(companySearchTerm)}`,
  );
  const combinedData = [...importer, ...manufacturers];

  function handleCompanySearch(value: string) {
    setPage(1);
    setCompanySearchTerm(value);
  }

  // Server-side, so it searches the supplier's whole shelf rather than the 25
  // rows on screen: `company_products` runs `Product.filter(filter_params)`,
  // whose `filter_by_search_term` matches brand name, medex code and the ATC
  // code's identifier/title/readable name. Table debounces the input for us.
  function handleProductSearch(value: string) {
    setProductPage(1);
    setProductSearchTerm(value);
  }

  const apiCall = useApiCallHandling();
  const navigate = useNavigate();

  useEffect(() => {
    setTotalPages(metaData?.total_pages);
  }, [metaData?.total_pages]);

  // USEFFECTS
  useEffect(() => {
    // Nothing to ask for until a supplier is picked — `set_company` on the
    // backend does `Company.find(params[:company_id])`, so firing this on
    // mount was only ever a 404.
    if (!selectedCompanyId) return;

    getProducts({
      method: "GET",
      url:
        COMPANY_PRODUCTS +
        `?company_id=${selectedCompanyId}&page=${productPage}&per_page=${productPerPage}` +
        `&search_term=${encodeURIComponent(productSearchTerm)}`,
    });
    //  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retry, selectedCompanyId, productPage, productPerPage, productSearchTerm]);

  useEffect(() => {
    if (typeof productsMeta?.total_pages === "number") {
      setProductTotalPages(productsMeta.total_pages);
    }
  }, [productsMeta]);

  // Accumulate rather than replace: a page that has scrolled out of view
  // still has to be resolvable, because its ticked rows are still selected.
  useEffect(() => {
    if (!fetchedProducts?.length) return;

    setProductsById((prev: KeyValueObj) => {
      const next = { ...prev };
      fetchedProducts.forEach((product: KeyValueObj) => {
        if (product?.id) next[product.id] = product;
      });
      return next;
    });
  }, [fetchedProducts]);

  // Selecting a manufacturer/importer opens the order modal straight into
  // product selection (stage 1) instead of revealing the products table
  // inline on the page.
  useEffect(() => {
    if (!selectedCompany?.length) return;

    setModalTitle("New Order");
    setModalSubtitle("Select products");
    setStage(1);
    setShowModal(true);
  }, [selectedCompany]);

  useEffect(() => {
    const tempTD = fetchedProducts?.map((product: KeyValueObj) => ({
      id: product?.id,
      code: {
        type: "string",
        value: product?.medex_code,
      },
      brand: {
        type: "string",
        value: product?.brand_name,
      },
      atc_name: {
        type: "string",
        value: `${product?.atc_code?.readable_name}`,
      },
      classification: {
        type: "string",
        value: product?.classification,
      },
      form: {
        type: "string",
        value: product?.form,
      },
      strength: {
        type: "string",
        value: product?.strength + product?.strength_unit,
      },
    }));
    setTableData(tempTD);
  }, [fetchedProducts]);

  // Validation for stage 2: every selected product needs at least one batch
  // chosen, every chosen batch needs a quantity greater than zero, and that
  // quantity can't exceed the batch's own in-stock count (`batch.quantity`,
  // set from the same batch the qty is picked against in ReqForm) — the
  // backend already rejects this at order creation, this just stops the
  // user getting that far. `order.selected_batches` is what ReqForm's batch
  // picker actually writes, there is no top-level `order.quantity`.
  useEffect(() => {
    const allProductsComplete = selectedProducts.every((product) => {
      const batches = orders[product?.id]?.selected_batches ?? [];
      return (
        batches.length > 0 &&
        batches.every((batch: KeyValueObj) => {
          const amount = Number(batch?.amount);
          return amount > 0 && amount <= Number(batch?.quantity);
        })
      );
    });

    setIsOrdersValid(selectedProducts.length > 0 && allProductsComplete);
  }, [orders, selectedProducts]);

  // Validation for delivery details — stage 3. `deliveryDetails` is the
  // AddressManager-selected address merged with the date/note fields, so
  // the address itself is confirmed via `.id` (also what's later sent as
  // `shipping_address_id`) — there is no `shipping_address` field on it.
  useEffect(() => {
    setIsDeliveryValid(
      !!deliveryDetails?.id &&
        !!deliveryDetails?.expected_delivery_date &&
        !beforeToday(deliveryDetails?.expected_delivery_date),
    );
  }, [deliveryDetails]);

  function closeModal() {
    setShowModal(false);
    setStage(1);
    setOrders({});
    setDeliveryDetails({});
  }

  function handleContinue() {
    // Resolved against every page loaded for this supplier, not against
    // `fetchedProducts` — a row ticked on page one is not in the page in view
    // once the buyer has paged forward or searched, and `.find` over the
    // current page pushed an `undefined` into this list instead.
    const selectedInv: KeyValueObj[] = selectedList
      .map((list: KeyValueObj) => productsById[list?.id])
      .filter(Boolean);

    setSelectedProducts(selectedInv);
    setProductIndex(0);
    setModalTitle("New Order");
    setModalSubtitle("Product details");
    setStage(2);
  }

  function backToProducts() {
    setModalTitle("New Order");
    setModalSubtitle("Select products");
    setStage(1);
  }

  function handleProductDetailsContinue() {
    setModalTitle("New Order");
    setModalSubtitle("Delivery details");
    setStage(3);
  }

  function backToProductDetails() {
    setModalTitle("New Order");
    setModalSubtitle("Product details");
    setStage(2);
  }

  function onOrderContinue() {
    setModalTitle("Purchase order summary");
    setModalSubtitle("Product details");
    setStage(4);
  }

  function orderBack() {
    setModalTitle("New Order");
    setModalSubtitle("Delivery details");
    setStage(3);
  }

  async function onOrderSubmit() {
    setLoading(true);
    // MED-707: source the payload from `selectedProducts`, not
    // `Object.keys(orders)` — `orders` only ever gains entries (ReqForm
    // writes to it per product but nothing ever deletes a key), so an
    // unticked-then-never-retouched product's stale entry stuck around and
    // rode along into the order. `selectedProducts` is rebuilt from scratch
    // on every stage-1 "Continue" click (see handleContinue) and is already
    // what the stage-2 validity check reads, so this makes it the one
    // source of truth for "what's in the order" everywhere.
    const products = selectedProducts.map((product: KeyValueObj) => {
      const orderKey = product?.id;

      // MED-706: the line is built by buildOrderLine so the supplier wizard and
      // the catalogue quick-order produce identical OrderProduct rows — it
      // converts each batch's amount to base units per batch (the readBatchUnit
      // multiplier, not the product's base_unit_count) at the API boundary.
      return buildOrderLine({
        product,
        batches: (orders?.[orderKey]?.selected_batches ?? []).map(
          (batch: { id: string; amount: number; multiplier: number }) => ({
            inventory_id: batch.id,
            amount: batch.amount,
            multiplier: batch.multiplier,
          }),
        ),
      });
    });

    const payload = {
      ...deliveryDetails,
      shipping_address_id: deliveryDetails?.id,
      supplier_id: selectedCompany?.[0]?.id,
      products,
    };

    apiCall(
      ORDERS_URL,
      "POST",
      { ...payload },
      // @ts-ignore
      (data: any) => {
        closeModal();
        setSelectedList([]);
        navigate("/dashboard/requisition");
      },
      (error: any) => {
        console.log(error);
      },
      () => {
        setLoading(false);
      },
    );
  }

  return (
    <ReqWrapper>
      <PageTitleNavs
        title="Create Order"
        backLink="/dashboard/requisition"
        bcrumbs={[
          { title: "Order", link: "/dashboard/requisition" },
          { title: "Create", link: "#" },
        ]}
      />

      <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <Tab
          tabsTitle={tabs}
          onTabClick={setView}
          currentTab={view}
          paramKey="view"
        />

        {view === "order by manufacturer" && (
          <div className="table-wrapper" style={{ marginTop: 0 }}>
            <Table
              tableHeaders={manuImporterTableHeaders}
              tableData={getManuImporterTableData(combinedData)}
              tableKeys={manuImporterTableKeys}
              actionsData={[]}
              full
              withCheck
              isRadio
              selectRowOnClick
              shouldHover
              setSelectedList={setSelectedCompany}
              selectedList={selectedCompany}
              loading={manufacturersLoading || impotersLoading}
              handleSearch={handleCompanySearch}
              rowLineColor="light"
              tableTitle="Manufactuers and Importers"
              tableSubtitle="Select a Manufacturer or a importer to see their products"
              errorLoading={
                manufacturersError?.length > 0 || importersError?.length > 0
              }
              onRetry={() => {
                setRetry((prev) => prev + 1);
              }}
            />
          </div>
        )}
      </div>

      {view === "all products" && <AllProductsOrder />}

      {view === "order by manufacturer" && (
        <>
      <Modal
        title={modalTitle}
        subTitle={modalSubtitle}
        isOpen={showModal}
        close={closeModal}
      >
        <ModalWrapper>
          <ModalFormWrapper show={stage === 1}>
            <div className="table-wrapper">
              <Table
                // Remounted per supplier so the table's own (uncontrolled)
                // search box empties along with `productSearchTerm`.
                key={selectedCompanyId}
                tableHeaders={tableHeaders}
                tableData={tableData}
                actionsData={[]}
                tableKeys={tableKeys}
                full
                withCheck
                rowLineColor="light"
                setSelectedList={setSelectedList}
                selectedList={selectedList}
                tableTitle="Manufacturer/Importer's Products"
                tableSubtitle={
                  selectedList?.length > 0
                    ? `${pluralize(selectedList.length, "product")} selected`
                    : "Select a Products and click continue to start the order process"
                }
                handleSearch={handleProductSearch}
                isSearching={productSearchTerm.length > 0}
                loading={productsLoading}
                errorLoading={productsError?.message?.length > 0}
                onRetry={() => {
                  setRetry((prev) => prev + 1);
                }}
              />
              <Pagination
                // Keyed so the pager's own highlighted page falls back to 1
                // whenever the result set it describes changes underneath it.
                key={`${selectedCompanyId}-${productSearchTerm}-${productPerPage}`}
                pageCount={productTotalPages || 1}
                onNextPage={(event) => setProductPage(event.selected)}
                onPerPageChange={(event) => {
                  setProductPage(1);
                  setProductPerPage(Number(event?.target?.value));
                }}
              />
            </div>
            <Button onClick={handleContinue} disabled={!!!selectedList?.length}>
              Continue
            </Button>
          </ModalFormWrapper>
          <ModalFormWrapper show={stage === 2}>
            {selectedProducts.length > 0 && (
              <>
                <ProductCarouselTrack
                  products={selectedProducts}
                  activeIndex={productIndex}
                >
                  {(product) => (
                    <ReqForm
                      product={product}
                      orders={orders}
                      setOrders={setOrders}
                    />
                  )}
                </ProductCarouselTrack>
                <ProductCarouselNav
                  products={selectedProducts}
                  activeIndex={productIndex}
                  onIndexChange={setProductIndex}
                />
              </>
            )}
            <div className="button-wrapper">
              <Button onClick={backToProducts} secondary>
                Back
              </Button>
              <Button
                onClick={handleProductDetailsContinue}
                disabled={!isOrdersValid}
              >
                Continue
              </Button>
            </div>
          </ModalFormWrapper>
          <ModalFormWrapper show={stage === 3}>
            <OrderDeliveryDetails
              deliveryDetails={deliveryDetails}
              setDeliveryDetails={setDeliveryDetails}
            />
            <div className="button-wrapper">
              <Button onClick={backToProductDetails} secondary>
                Back
              </Button>
              <Button onClick={onOrderContinue} disabled={!isDeliveryValid}>
                Continue
              </Button>
            </div>
          </ModalFormWrapper>
          <ModalFormWrapper show={stage === 4}>
            {selectedProducts.length > 0 && (
              <>
                <ProductCarouselTrack
                  products={selectedProducts}
                  activeIndex={productIndex}
                >
                  {(product) => (
                    <ReqForm
                      product={product}
                      orders={orders}
                      setOrders={setOrders}
                      type="preview"
                    />
                  )}
                </ProductCarouselTrack>
                <ProductCarouselNav
                  products={selectedProducts}
                  activeIndex={productIndex}
                  onIndexChange={setProductIndex}
                />
              </>
            )}

            <OrderDeliveryDetails
              deliveryDetails={deliveryDetails}
              setDeliveryDetails={setDeliveryDetails}
              isPreview
            />
            <div className="button-wrapper">
              <Button onClick={orderBack} secondary>
                Back
              </Button>
              <Button
                loading={loading}
                onClick={onOrderSubmit}
                disabled={loading}
              >
                Submit
              </Button>
            </div>
          </ModalFormWrapper>
        </ModalWrapper>
      </Modal>

      <Pagination
        pageCount={totalPages || 1}
        onNextPage={(event) => setPage(event.selected)}
        onPerPageChange={(event) => setPerPage(Number(event?.target?.value))}
      />
        </>
      )}
    </ReqWrapper>
  );
}
